import React, { useState, useEffect } from 'react';
import { X, Trash2 } from 'lucide-react';
import config from '../config.json';

const TaskModal = ({ task, onClose, onSave, onDelete }) => {
  const [activeTab, setActiveTab] = useState('client');
  const [formData, setFormData] = useState({});

  // Filter visible tabs and sort by order
  const tabs = config.modalTabs
    .filter(tab => !tab.hidden)
    .sort((a, b) => a.order - b.order);

  useEffect(() => {
    if (task) {
      setFormData(task);
    } else {
      // Initialize with default values from config
      const defaultData = {};
      Object.keys(config.formFields).forEach(tabId => {
        config.formFields[tabId].forEach(field => {
          if (field.defaultValue !== undefined) {
            defaultData[field.id] = field.defaultValue;
          }
        });
      });
      setFormData(defaultData);
    }
  }, [task]);

  const handleSubmit = () => {
    onSave(formData);
  };

  const handleFieldChange = (fieldId, value) => {
    setFormData({ ...formData, [fieldId]: value });
  };

  const renderField = (field) => {
    const value = formData[field.id];

    switch (field.type) {
      case 'text':
      case 'email':
      case 'tel':
        return (
          <div key={field.id}>
            <label className="block text-sm font-medium text-muted mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type={field.type}
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className="w-full bg-background text-foreground px-4 py-2.5 rounded-lg border border-border focus:outline-none focus:border-[#6366f1] transition-colors"
              placeholder={field.placeholder}
              required={field.required}
            />
          </div>
        );

      case 'date':
        return (
          <div key={field.id}>
            <label className="block text-sm font-medium text-muted mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="date"
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className="w-full bg-background text-foreground px-4 py-2.5 rounded-lg border border-border focus:outline-none focus:border-[#6366f1] transition-colors"
              required={field.required}
            />
          </div>
        );

      case 'number':
        return (
          <div key={field.id}>
            <label className="block text-sm font-medium text-muted mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="number"
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className="w-full bg-background text-foreground px-4 py-2.5 rounded-lg border border-border focus:outline-none focus:border-[#6366f1] transition-colors"
              placeholder={field.placeholder}
              required={field.required}
            />
          </div>
        );

      case 'select':
        return (
          <div key={field.id}>
            <label className="block text-sm font-medium text-muted mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              value={value || ''}
              onChange={(e) => handleFieldChange(field.id, e.target.value)}
              className="w-full bg-background text-foreground px-4 py-2.5 rounded-lg border border-border focus:outline-none focus:border-[#6366f1] transition-colors"
              required={field.required}
            >
              {field.options.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        );

      case 'radio':
        return (
          <div key={field.id} className="mb-4">
            <label className="block text-sm font-medium text-muted mb-2">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="flex gap-4">
              {field.options.map((option) => (
                <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={field.id}
                    value={option.value}
                    checked={value === option.value}
                    onChange={(e) => handleFieldChange(field.id, option.value)}
                    className="w-4 h-4 text-[#6366f1]"
                  />
                  <span className="text-foreground">{option.label}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'checkbox':
        return (
          <label key={field.id} className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => handleFieldChange(field.id, e.target.checked)}
              className="w-4 h-4 text-[#6366f1] rounded"
            />
            <span className="text-foreground text-sm">{field.label}</span>
          </label>
        );

      case 'toggle':
        return (
          <div key={field.id} className="flex items-center gap-3 mb-4">
            <input
              type="checkbox"
              checked={value || false}
              onChange={(e) => handleFieldChange(field.id, e.target.checked)}
              className="w-10 h-6 appearance-none bg-gray-300 rounded-full relative cursor-pointer transition-colors checked:bg-[#6366f1] before:content-[''] before:absolute before:w-5 before:h-5 before:rounded-full before:bg-white before:top-0.5 before:left-0.5 before:transition-transform checked:before:translate-x-4"
            />
            <label className="text-foreground font-medium">{field.label}</label>
          </div>
        );

      case 'info':
        return (
          <div key={field.id} className="mb-4">
            <label
              className="block text-sm font-medium mb-2"
              style={{ color: field.color || '#00bcd4' }}
            >
              {field.value}
            </label>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-border flex flex-col shadow-2xl">
        {/* Tabs Header */}
        <div className="border-b border-border bg-sidebar">
          <div className="flex items-center overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-[#e91e63] text-foreground bg-card'
                    : 'border-transparent text-muted hover:text-foreground hover:bg-card-hover'
                }`}
              >
                <span>{tab.icon}</span>
                {tab.label}
              </button>
            ))}
            <div className="ml-auto px-4">
              <button
                onClick={onClose}
                className="p-2 hover:bg-card-hover rounded-lg transition-colors text-muted hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {config.formFields[activeTab] && config.formFields[activeTab].length > 0 ? (
            <div className="space-y-6">
              {config.formFields[activeTab].filter(field => !field.hidden).map((field) => renderField(field))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted">Contenu de l'onglet {tabs.find(t => t.id === activeTab)?.label}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-sidebar">
          {task && (
            <button
              onClick={() => onDelete(task.id)}
              className="px-4 py-2.5 bg-[#ef4444] hover:bg-[#dc2626] text-white rounded-lg transition-colors font-medium flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Supprimer
            </button>
          )}
          <button
            className="px-6 py-2.5 bg-[#00bcd4] hover:bg-[#00acc1] text-white rounded-lg transition-colors font-medium"
            onClick={handleSubmit}
          >
            ENREGISTRER
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;
